import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiTrash2, FiCheck, FiEye } from 'react-icons/fi';
import {
  getActiveCarriers, getCarrierDownloadRules, createCarrierDownloadRule,
  updateCarrierDownloadRule, deleteCarrierDownloadRule,
  SAMPLE_CARRIERS, type Carrier, type CarrierDownloadRule, type CarrierRuleAction,
} from '../../services/carrierDownloadService';

const ACTION_LABELS: Record<CarrierRuleAction, string> = {
  review_remarks: 'Look into remarks',
  remarks_to_notes: 'Pull remarks into notes',
  direct_merge: 'Direct Merge',
  ai_merge: 'AI Merge',
  user_review: 'User Review',
};

const DEFAULT_TITLES: Partial<Record<CarrierRuleAction, string>> = {
  review_remarks: 'Look into the remarks',
  remarks_to_notes: 'Pull remarks into notes',
};

// Local draft: new (unsaved) rules have no id.
type Draft = Omit<CarrierDownloadRule, 'id'> & { id?: string; dirty?: boolean; saving?: boolean };

const CarrierDownloadRules: React.FC = () => {
  const [carriers, setCarriers]       = useState<Carrier[]>([]);
  const [carrierId, setCarrierId]     = useState('');
  const [rules, setRules]             = useState<Draft[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [search, setSearch]           = useState('');

  useEffect(() => {
    getActiveCarriers()
      .catch(() => SAMPLE_CARRIERS) // front-end-only testing: backend carriers unavailable
      .then(list => {
        const carriers = list.length ? list : SAMPLE_CARRIERS;
        setCarriers(carriers);
        setCarrierId(carriers[0].id);
      });
  }, []);

  const loadRules = useCallback((id: string) => {
    setLoading(true);
    setError(null);
    getCarrierDownloadRules(id)
      .then(setRules)
      .catch(() => { setRules([]); setError('Could not load rules for this carrier.'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (carrierId) loadRules(carrierId); }, [carrierId, loadRules]);

  const carrier = carriers.find(c => c.id === carrierId);
  const visibleCarriers = carriers.filter(c => c.name.toLowerCase().includes(search.trim().toLowerCase()));
  const hasRemarksRule = rules.some(r => r.action === 'review_remarks');

  const patch = (idx: number, changes: Partial<Draft>) =>
    setRules(rs => rs.map((r, i) => (i === idx ? { ...r, ...changes, dirty: true } : r)));

  const addRule = (action: CarrierRuleAction) =>
    setRules(rs => [...rs, {
      carrierId,
      title: DEFAULT_TITLES[action] ?? '',
      action,
      remarks: '',
      matchText: '',
      enabled: true,
      dirty: true,
    }]);

  const saveRule = async (idx: number) => {
    const { id, dirty, saving, ...input } = rules[idx];
    setRules(rs => rs.map((r, i) => (i === idx ? { ...r, saving: true } : r)));
    setError(null);
    try {
      const saved = id
        ? await updateCarrierDownloadRule(id, input)
        : await createCarrierDownloadRule(input);
      setRules(rs => rs.map((r, i) => (i === idx ? saved : r)));
    } catch {
      setError('Could not save the rule. Please try again.');
      setRules(rs => rs.map((r, i) => (i === idx ? { ...r, saving: false } : r)));
    }
  };

  const removeRule = async (idx: number) => {
    const { id } = rules[idx];
    if (id) {
      try { await deleteCarrierDownloadRule(id); }
      catch { setError('Could not delete the rule. Please try again.'); return; }
    }
    setRules(rs => rs.filter((_, i) => i !== idx));
  };

  return (
    <div className="cdsl-card cdr-rules cdr-rules--split">
      <aside className="cdr-carriers">
        <input
          className="cdr-rules__input"
          placeholder="Search carriers"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <ul className="cdr-carriers__list">
          {visibleCarriers.map(c => (
            <li key={c.id}>
              <button
                className={`cdr-carriers__item${c.id === carrierId ? ' cdr-carriers__item--active' : ''}`}
                onClick={() => setCarrierId(c.id)}
              >
                {c.name}
              </button>
            </li>
          ))}
          {visibleCarriers.length === 0 && <li className="cdr-rules__hint">No carriers found.</li>}
        </ul>
      </aside>

      <section className="cdr-rules__detail">
      <div className="cdr-rules__bar">
        <h3 className="cdr-rules__title">{carrier ? `${carrier.name} rules` : 'Select a carrier'}</h3>
        <div className="cdr-rules__actions">
          {!hasRemarksRule && carrierId && (
            <button className="cdr-rules__btn" onClick={() => addRule('review_remarks')}>
              <FiEye size={14} /> Add “Look into remarks” rule
            </button>
          )}
          <button className="cdr-rules__btn" onClick={() => addRule('remarks_to_notes')} disabled={!carrierId}>
            <FiPlus size={14} /> Pull remarks into notes
          </button>
          <button className="cdr-rules__btn" onClick={() => addRule('user_review')} disabled={!carrierId}>
            <FiPlus size={14} /> Add rule
          </button>
        </div>
      </div>

      {carrier && (
        <p className="cdr-rules__hint">
          This is <strong>{carrier.name}</strong>. These rules apply only to {carrier.name} downloads.
        </p>
      )}
      {error && <div className="cdr-alert cdr-alert--error">{error}</div>}

      {loading ? (
        <div className="cdsl-skeleton">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="cdsl-skeleton-row" />)}
        </div>
      ) : rules.length === 0 ? (
        <div className="cdsl-empty">No rules for this carrier yet.</div>
      ) : (
        rules.map((rule, idx) => (
          <div key={rule.id ?? `new-${idx}`} className="cdr-rule">
            <div className="cdr-rule__row">
              <input
                className="cdr-rules__input"
                placeholder="Rule name"
                value={rule.title}
                onChange={e => patch(idx, { title: e.target.value })}
              />
              <select
                className="cdr-rules__select"
                value={rule.action}
                onChange={e => patch(idx, { action: e.target.value as CarrierRuleAction })}
              >
                {(Object.keys(ACTION_LABELS) as CarrierRuleAction[]).map(a =>
                  <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
              </select>
              <label className="cdr-rules__toggle">
                <input type="checkbox" checked={rule.enabled} onChange={e => patch(idx, { enabled: e.target.checked })} />
                Enabled
              </label>
            </div>
            {rule.action === 'remarks_to_notes' && (
              <input
                className="cdr-rules__input"
                placeholder="Only when the carrier remarks contain… (leave blank for every transaction)"
                value={rule.matchText}
                onChange={e => patch(idx, { matchText: e.target.value })}
              />
            )}
            <textarea
              className="cdr-rules__input cdr-rules__remarks"
              placeholder={rule.action === 'remarks_to_notes'
                ? "Remarks — how the pulled carrier remarks should read as a note (e.g. prefix or instructions)"
                : "Remarks — what to look for on this carrier's transactions"}
              rows={3}
              value={rule.remarks}
              onChange={e => patch(idx, { remarks: e.target.value })}
            />
            <div className="cdr-rule__row cdr-rule__row--end">
              <button
                className="cdr-rules__btn"
                onClick={() => saveRule(idx)}
                disabled={!rule.dirty || rule.saving || !rule.title.trim()}
              >
                <FiCheck size={14} /> {rule.saving ? 'Saving…' : 'Save'}
              </button>
              <button className="cdr-rules__btn cdr-rules__btn--danger" onClick={() => removeRule(idx)}>
                <FiTrash2 size={14} /> Delete
              </button>
            </div>
          </div>
        ))
      )}
      </section>
    </div>
  );
};

export default CarrierDownloadRules;
